import * as React from 'react'
import { offset, shift } from '@floating-ui/react'
import { useUiEditorState } from '@/tiptap/hooks/use-ui-editor-state'
// --- Utils ---
import { isSelectionValid } from '@/tiptap/lib/tiptap-collab-utils'
// --- Primitive UI Components ---
import type { ButtonProps } from '@/tiptap/tiptap-ui-primitive/button'
import { Button } from '@/tiptap/tiptap-ui-primitive/button'
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from '@/tiptap/tiptap-ui-primitive/toolbar'
// --- UI Utils ---
import { FloatingElement } from '@/tiptap/tiptap-ui-utils/floating-element'
// --- UI ---
import { ColorTextPopover } from '@/tiptap/tiptap-ui/color-text-popover'
import type { Mark } from '@/tiptap/tiptap-ui/mark-button'
import { canToggleMark, MarkButton } from '@/tiptap/tiptap-ui/mark-button'
import { isNodeSelection, type Editor } from '@tiptap/react'
import { RemoveFormatting, Bot } from 'lucide-react'
import { Sparkles, MessageSquare, Lightbulb } from 'lucide-react'
import { useIsMobile } from '@/hooks/use-mobile'
// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor'
import { AISelector, AICompletionResolver, EditorContext } from './ai-selector'
import { FeatureGate } from '@/components/plan-restrictions/FeatureGate'

const BLOCK_SELECTOR = [
  'p[id]',
  'p[data-id]',
  'h1[id]',
  'h1[data-id]',
  'h2[id]',
  'h2[data-id]',
  'h3[id]',
  'h3[data-id]',
  'h4[id]',
  'h4[data-id]',
  'h5[id]',
  'h5[data-id]',
  'h6[id]',
  'h6[data-id]',
].join(', ')

// eslint-disable-next-line react-refresh/only-export-components
export function resolveSelectedClaimContext(editor: Editor | null): {
  docBlockId: string
  quoteText: string
  offset: number
} | null {
  if (!editor) return null

  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return null
  }

  const quoteText = selection.toString().trim()
  if (!quoteText) return null

  const range = selection.getRangeAt(0)
  const commonNode = range.commonAncestorContainer
  const editorElement = editor.view.dom
  if (!editorElement.contains(commonNode)) return null

  const selectionRoot =
    commonNode.nodeType === Node.ELEMENT_NODE
      ? (commonNode as Element)
      : commonNode.parentElement
  const blockElement = selectionRoot?.closest(BLOCK_SELECTOR)
  if (!blockElement || !editorElement.contains(blockElement)) {
    return null
  }

  const docBlockId =
    blockElement.getAttribute('id') ?? blockElement.getAttribute('data-id')
  if (!docBlockId) return null

  // Calculate offset: character position of selected text within the block
  const blockText = blockElement.textContent ?? ''
  const blockRange = document.createRange()
  blockRange.selectNodeContents(blockElement)
  blockRange.setEnd(range.endContainer, range.endOffset)
  const selectedStartOffset = blockRange.toString().length - quoteText.length
  const offset = Math.max(0, selectedStartOffset)

  return { docBlockId, quoteText, offset }
}

export function NotionToolbarFloating({
  completeResolver,
  enableComments = false,
  onContinueInChat,
  onAddClaimFromSelection,
  onAskVera,
  hideFormatting = false,
  commentActionLabel,
}: {
  completeResolver?: AICompletionResolver
  enableComments?: boolean
  onContinueInChat?: (context: EditorContext) => void
  onAddClaimFromSelection?: (payload: {
    docBlockId: string
    quoteText: string
    offset: number
  }) => void
  onAskVera?: (payload: {
    quoteText?: string
    docBlockId?: string
  }) => void
  /** Review / read mode: drop the formatting marks. Ask Vera, Add Claim and
      Comment still render when their handler / flag is wired. */
  hideFormatting?: boolean
  commentActionLabel?: string
}) {
  const { editor } = useTiptapEditor()
  const isMobile = useIsMobile()
  const { lockDragHandle, aiGenerationActive, commentInputVisible } =
    useUiEditorState(editor)
  const [shouldShow, setShouldShow] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [aiMode, setAiMode] = React.useState<'selection' | 'ask'>('selection')
  const preventCloseOnSelectionUpdate = React.useRef(false)
  const selectedClaimContext = resolveSelectedClaimContext(editor)

  React.useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      const { state } = editor
      const { selection } = state

      // Don't auto-close if we're preventing close (e.g., just opened from slash command)
      if (preventCloseOnSelectionUpdate.current) {
        preventCloseOnSelectionUpdate.current = false
        return
      }

      setOpen(false)

      const validSelection =
        isSelectionValid(editor, selection) &&
        (!isNodeSelection(selection) ||
          (isNodeSelection(selection) && selection.node.type.name === 'image'))

      if (commentInputVisible || aiGenerationActive) {
        setShouldShow(false)
        return
      }

      setShouldShow(validSelection)
    }

    handleSelectionUpdate()

    editor.on('selectionUpdate', handleSelectionUpdate)

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate)
    }
  }, [editor, aiGenerationActive, commentInputVisible])

  // Listen for openAiSelector event from slash command
  React.useEffect(() => {
    if (!editor) return

    const handleOpenAiSelector = ({ mode }: { mode: 'selection' | 'ask' }) => {
      // The inline AI selector needs a completion resolver to do anything;
      // without one (e.g. the vault draft editor, which mounts this toolbar
      // only for formatting) ignore the open request so we never render the
      // selector against an undefined resolver.
      if (!completeResolver) return
      setAiMode(mode || 'selection')
      setOpen(true)
      // For ask mode triggered from slash, force shouldShow to true
      if (mode === 'ask') {
        setShouldShow(true)
        // Prevent the next selection update from closing the AI selector
        preventCloseOnSelectionUpdate.current = true
      }
    }

    // @ts-expect-error - custom event
    editor.on('openAiSelector', handleOpenAiSelector)

    return () => {
      // @ts-expect-error - custom event
      editor.off('openAiSelector', handleOpenAiSelector)
    }
  }, [editor, completeResolver])

  // Capture cursor position when opening in ask mode
  const [askModeCursorRect, setAskModeCursorRect] = React.useState<DOMRect | null>(null)

  // if (
  //   !editor.isEditable ||
  //   editor.isActive('image') ||
  //   empty ||
  //   isNodeSelection(selection) ||
  //   editor.isActive('codeBlock')
  // ) {
  //   return false
  // }
  // return true

  if (lockDragHandle || isMobile) {
    return null
  }

  // Render AI selector standalone when in ask mode (triggered from slash
  // command). Requires a completion resolver — guarded so a resolver-less
  // formatting-only mount never reaches the `completeResolver!` assertion.
  if (open && aiMode === 'ask' && completeResolver) {
    // Get cursor position for positioning the AI selector (memoized on first open)
    const getCursorPosition = (editor: Editor) => {
      if (askModeCursorRect) {
        return askModeCursorRect
      }
      const { state } = editor
      const { from } = state.selection
      const coords = editor.view.coordsAtPos(from)
      const rect = new DOMRect(coords.left, coords.top, 0, coords.bottom - coords.top)
      setAskModeCursorRect(rect)
      return rect
    }

    return (
      <FloatingElement
        shouldShow={true}
        floatingOptions={{
          placement: 'top',
          middleware: [offset(-2), shift()],
        }}
        getBoundingClientRect={getCursorPosition}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setOpen(false)
            setShouldShow(false)
            setAiMode('selection')
            setAskModeCursorRect(null) // Reset position when closing
          }
        }}
      >
        <AISelector
          editor={editor}
          open={open}
          onOpenChange={(isOpen) => {
            setOpen(isOpen)
            if (!isOpen) {
              setShouldShow(false)
              setAiMode('selection')
              setAskModeCursorRect(null) // Reset position when closing
            }
          }}
          completeResolver={completeResolver}
          onContinueInChat={onContinueInChat}
          mode={aiMode}
        />
      </FloatingElement>
    )
  }

  return (
    <FloatingElement
      shouldShow={shouldShow}
      floatingOptions={{
        placement: 'top',
        middleware: [offset(-2), shift()],
      }}
    >
      {open && completeResolver ? (
        <AISelector
          editor={editor}
          open={open}
          onOpenChange={setOpen}
          completeResolver={completeResolver}
          onContinueInChat={onContinueInChat}
          mode={aiMode}
        />
      ) : (
        <Toolbar
          variant='floating'
          className='[--tt-toolbar-bg-color:#ffffff] [--tt-toolbar-border-color:#e5e7eb] dark:[--tt-toolbar-bg-color:var(--popover)] dark:[--tt-toolbar-border-color:var(--border)]'
          style={{
            borderRadius: 'var(--radius-md)',
          }}
        >
          {/* AI/claim actions group — omitted entirely when neither is wired
              (e.g. the formatting-only vault draft) so the toolbar doesn't
              open with an empty group + dangling separator. */}
          {(onAskVera || onAddClaimFromSelection) && (
          <ToolbarGroup>
            {/* "Ask Vera" button — only when an Ask-Vera handler is wired.
                Editors that mount this toolbar purely for formatting (e.g. the
                vault draft) pass none, so it's hidden there. */}
            {onAskVera && (
              <Button
                type='button'
                data-style='ghost'
                className='w-fit gap-1.5 !rounded-md border-none h-7 px-2'
                onMouseDown={(event) => {
                  event.preventDefault()
                }}
                onClick={() => {
                  if (!editor) return
                  const currentSelection = resolveSelectedClaimContext(editor)
                  const { from, to } = editor.state.selection
                  const quoteText =
                    currentSelection?.quoteText ??
                    editor.state.doc.textBetween(from, to, '\n').trim()

                  onAskVera({
                    quoteText: quoteText || undefined,
                    docBlockId: currentSelection?.docBlockId,
                  })
                  setShouldShow(false)
                }}
                title='Ask Vera AI for help'
              >
                <Bot className='h-3.5 w-3.5 text-cobalt-strong' />
                <span className='text-xs whitespace-nowrap'>Ask Vera</span>
              </Button>
            )}
            {onAddClaimFromSelection ? (
              <>
                {onAskVera && <ToolbarSeparator />}
                <Button
                  type='button'
                  data-style='ghost'
                  className='w-fit gap-1.5 !rounded-md border-none h-7 px-2'
                  onMouseDown={(event) => {
                    event.preventDefault()
                  }}
                  onClick={() => {
                    if (!editor) return
                    const currentSelection = resolveSelectedClaimContext(editor)
                    if (!currentSelection) return

                    onAddClaimFromSelection({
                      docBlockId: currentSelection.docBlockId,
                      quoteText: currentSelection.quoteText,
                      offset: currentSelection.offset,
                    })
                    setShouldShow(false)
                  }}
                >
                  <Lightbulb className='h-3.5 w-3.5' />
                  <span className='text-xs whitespace-nowrap'>Add Claim</span>
                </Button>
              </>
            ) : null}
          </ToolbarGroup>
          )}

          {enableComments && (
            <>
              {(onAskVera || onAddClaimFromSelection) && <ToolbarSeparator />}
              <ToolbarGroup>
                <FeatureGate flag='comments' >
                  <Button
                    type='button'
                    data-style='ghost'
                    className='w-fit gap-1.5 !rounded-md border-none h-7 px-2'
                    onClick={() => {
                      if (!editor) {
                        return
                      }
                      editor.chain().focus().setComment('').run()
                    }}
                    tooltip={commentActionLabel ?? 'Add comment'}
                  >
                    <MessageSquare className='h-3.5 w-3.5' />
                    <span className='text-xs whitespace-nowrap'>
                      {commentActionLabel ?? 'Comment'}
                    </span>
                  </Button>
                </FeatureGate>
              </ToolbarGroup>
            </>
          )}

          {!hideFormatting && (
            <>
              {/* Only separate the formatting marks from preceding groups when
                  there actually is a preceding group. */}
              {(onAskVera || onAddClaimFromSelection || enableComments) && (
                <ToolbarSeparator />
              )}

              <ToolbarGroup>
                <MarkButton type='bold' hideWhenUnavailable={true} className='!h-7' />
                <MarkButton type='italic' hideWhenUnavailable={true} className='!h-7' />
                <MarkButton type='strike' hideWhenUnavailable={true} className='!h-7' />
                <MarkButton type='underline' hideWhenUnavailable={true} className='!h-7' />
                <MarkButton type='code' hideWhenUnavailable={true} className='!h-7' />
                <MarkButton type='kbd' hideWhenUnavailable={true} className='!h-7' />
                <Button
                  type='button'
                  onClick={() => editor?.chain().focus().unsetAllMarks().run()}
                  disabled={!editor?.can().unsetAllMarks()}
                  data-style='ghost'
                  className='h-7 w-7 !rounded-md p-0 disabled:opacity-50'
                  tooltip='Clear formatting'
                  shortcutKeys='Mod-\\'
                >
                  <RemoveFormatting className='h-3.5 w-3.5' />
                </Button>
              </ToolbarGroup>
            </>
          )}
        </Toolbar>
      )}
    </FloatingElement>
  )
}
